import { Mesh } from 'three/src/objects/Mesh';
import { defaultFillAnchor } from './stopeGeometry';
import { getFillCurve } from './fillSurface';

/**
 * Rise rate over time for a stope's fill sequence.
 *
 * THE MODEL. The fill surface is taken as HORIZONTAL. A real paste beach is a shallow cone (1.25 degrees
 * by default), so this is close but not identical to what the 3D view draws - see the divergence note
 * below. A flat surface is what makes a rise rate meaningful in the first place: with a cone, different
 * points in the stope rise at different rates and at different times, so "the" rise rate would need a
 * reference point (normally the barricade) that this model has no knowledge of.
 *
 * THE MATHS. A FillCurve built at 0 degrees IS the stope's stage-storage curve: volumeAt(z) is the
 * volume below the horizontal plane z. The horizontal cross-sectional area is its derivative, so
 *
 *     riseRate(z) = Q / A(z),   A(z) = dV/dz
 *
 * and the time to place one slice is its volume over the flow rate. Both come from the same curve, so
 * the elapsed time across a pour's slices sums exactly to volume / Q - the duration already reported for
 * that pour elsewhere.
 *
 * WHY 0 DEGREES IS THE CHEAP CASE. refineForSurfaces splits nothing at grade 0 (a horizontal plane is
 * linear across any triangle, so there is no interpolation error to bound), and volumeAt is exact at any
 * tessellation. A stope carries 118-144 triangles, so a 40-slice series is a few thousand operations -
 * far cheaper than the sloped curves the fill view already builds, and it needs no refinement at all.
 *
 * DIVERGENCE FROM THE 3D VIEW. Levels here come from the flat curve; the volume table's heights come
 * from the real cone model (StopeFillStep.riseAtDischarge, measured at the discharge point). At the
 * 1.25 degree default the two agree closely. At a steep project default they will visibly disagree, so
 * present this graph's height on its own axis rather than implying it matches the table.
 *
 * THE OPENING SPIKE, AND `plotFromLevel`. Volume is measured from the bottom of the SOLID, because that
 * is where V = 0 and it is where paste run into a stope with a drawpoint or undercut lobe actually goes
 * first. But a lobe is narrow, so A(z) down there is tiny and Q / A(z) is enormous - a real geometric
 * fact that renders as a near-vertical spike holding almost no volume, squashing the rest of a multi-day
 * fill into a flat line. Measured on a real stope: 2.99 m/h through the opening metre against ~0.55 m/h
 * for the remaining 77 hours.
 *
 * `plotFromLevel` clips the EMITTED slices to that level and nothing else. Volume and elapsed time below
 * it still accumulate, so totalHours and totalVolume stay honest, and the first drawn slice simply
 * starts at the hour the fill reached that level. Callers pass the discharge floor, which is the datum
 * the rest of the fill view measures pour heights from, so the graph and the volume table agree on where
 * "the bottom" is. Slice volumes then sum to the DRAWN volume rather than to totalVolume.
 */

const DEFAULT_SLICE_HEIGHT = 1;
const MIN_SLICE_HEIGHT = 0.05;
const MAX_SLICES_PER_POUR = 2000;

export interface RiseRatePour {
  entityId: string;
  /** Volume this pour places, m3. */
  volume: number;
  /**
   * Volumetric flow rate, m3/h. Undefined for rock fill and for any recipe whose rheology cannot supply
   * one; such a pour still raises the fill (its slices carry a level) but contributes no elapsed time.
   */
  flowRate?: number;
}

export interface RiseRateOptions {
  /** Target slice thickness in metres. Slices are never thicker than this; default 1 m. */
  sliceHeight?: number;
  /**
   * Clip emitted slices to at or above this level. Volume and time below it still accumulate — see the
   * opening-spike note above. Defaults to no clipping.
   */
  plotFromLevel?: number;
}

export interface RiseRateSlice {
  pourEntityId: string;
  pourIndex: number;
  /** Elapsed pouring hours at the start and end of this slice. Equal when the flow rate is unknown. */
  fromHours: number;
  toHours: number;
  /** Fill surface elevation, in the stope geometry frame. */
  fromLevel: number;
  toLevel: number;
  /** Volume placed in this slice, m3. */
  volume: number;
  /** m/h over this slice. Undefined when the pour has no known flow rate. */
  riseRate?: number;
}

export interface RiseRateSeries {
  slices: RiseRateSlice[];
  /** The level at which the enclosed volume is zero - the bottom of the solid. Heights measure from here. */
  baseLevel: number;
  /** Elapsed pouring hours across every pour with a known flow rate. */
  totalHours: number;
  /** Volume placed across all pours, capped at what the stope can hold. */
  totalVolume: number;
  /** The stope's own volume, for scaling a height axis. */
  stopeVolume: number;
  peakRiseRate?: number;
  /**
   * True when at least one pour had no flow rate. The time axis then covers only the pours that did, so
   * the caller must say so rather than presenting it as the full fill duration.
   */
  hasUnknownFlowRate: boolean;
}

/**
 * Slices each pour's level band and prices every slice against that pour's own flow rate.
 *
 * Pours must already be in fill order (see sortPoursChronologically). Each pour's band runs from the
 * level holding everything placed before it to the level holding everything through it, so a pour placed
 * at a different flow rate simply steps the rate at its boundary - which is exactly the plug-then-main
 * case, with the plug's rate over its band and the main's over the one above.
 */
export const buildRiseRateSeries = (
  stope: Mesh,
  pours: readonly RiseRatePour[],
  { sliceHeight = DEFAULT_SLICE_HEIGHT, plotFromLevel = -Infinity }: RiseRateOptions = {},
): RiseRateSeries => {
  const curve = getFillCurve(stope, [], defaultFillAnchor(stope), 0);
  const step = Math.max(MIN_SLICE_HEIGHT, sliceHeight);
  const floor = Math.max(plotFromLevel, curve.minLevel);

  const slices: RiseRateSlice[] = [];
  let cumulativeVolume = 0;
  let hours = 0;
  let peakRiseRate: number | undefined;
  let hasUnknownFlowRate = false;

  pours.forEach((pour, pourIndex) => {
    const { flowRate } = pour;
    if (flowRate === undefined || flowRate <= 0) hasUnknownFlowRate = true;

    const startVolume = cumulativeVolume;
    const endVolume = Math.min(curve.totalVolume, startVolume + Math.max(0, pour.volume));
    if (endVolume <= startVolume) return;

    const startLevel = curve.levelAt(startVolume);
    const endLevel = curve.levelAt(endVolume);
    const span = endLevel - startLevel;
    if (span <= 0) return;

    const stepCount = Math.min(MAX_SLICES_PER_POUR, Math.max(1, Math.ceil(span / step)));
    // volumeAt is the authority at both ends, so the slices telescope to exactly this pour's volume even
    // where the level band was clamped by the stope running out of room.
    let sliceStartLevel = startLevel;
    let sliceStartVolume = startVolume;

    Array.from({ length: stepCount }).forEach((unused, index) => {
      const isLast = index === stepCount - 1;
      const sliceEndLevel = isLast ? endLevel : startLevel + (span * (index + 1)) / stepCount;
      const sliceEndVolume = isLast ? endVolume : curve.volumeAt(sliceEndLevel);
      const duration = flowRate !== undefined && flowRate > 0
        ? Math.max(0, sliceEndVolume - sliceStartVolume) / flowRate
        : undefined;

      // Below `floor` the fill is still being placed - it takes this volume and this long - it is just
      // not DRAWN, so `hours` advances either way and only the emitted part is clipped.
      if (sliceEndLevel > floor) {
        const drawnFromLevel = Math.max(sliceStartLevel, floor);
        const drawnFromVolume = drawnFromLevel === sliceStartLevel
          ? sliceStartVolume
          : curve.volumeAt(drawnFromLevel);
        const drawnVolume = Math.max(0, sliceEndVolume - drawnFromVolume);
        const drawnDuration = flowRate !== undefined && flowRate > 0 ? drawnVolume / flowRate : undefined;
        // A zero-volume slice has no duration to divide by; it means the band ran past the solid.
        const riseRate = drawnDuration !== undefined && drawnDuration > 0
          ? (sliceEndLevel - drawnFromLevel) / drawnDuration
          : undefined;

        if (riseRate !== undefined && (peakRiseRate === undefined || riseRate > peakRiseRate)) {
          peakRiseRate = riseRate;
        }

        slices.push({
          pourEntityId: pour.entityId,
          pourIndex,
          fromHours: hours + ((duration ?? 0) - (drawnDuration ?? 0)),
          toHours: hours + (duration ?? 0),
          fromLevel: drawnFromLevel,
          toLevel: sliceEndLevel,
          volume: drawnVolume,
          riseRate,
        });
      }

      hours += duration ?? 0;
      sliceStartLevel = sliceEndLevel;
      sliceStartVolume = sliceEndVolume;
    });

    cumulativeVolume = endVolume;
  });

  return {
    slices,
    baseLevel: floor,
    totalHours: hours,
    totalVolume: cumulativeVolume,
    stopeVolume: curve.totalVolume,
    peakRiseRate,
    hasUnknownFlowRate,
  };
};

export interface RiseRateBand {
  pourEntityId: string;
  pourIndex: number;
  slices: RiseRateSlice[];
  /** Undefined when this pour had no flow rate, so the band has nothing to plot. */
  peakRiseRate?: number;
}

/**
 * Groups slices into one band per pour, for a renderer that wants a series per pour.
 *
 * Runs of ADJACENT slices, not a group-by on the id: the same pour appearing twice in a fill sequence
 * must stay two bands, or a line gets drawn straight across whatever was poured in between.
 */
export const groupSlicesByPour = (slices: readonly RiseRateSlice[]): RiseRateBand[] => slices
  .reduce<RiseRateBand[]>((bands, slice) => {
    const current = bands[bands.length - 1];
    const band = current && current.pourEntityId === slice.pourEntityId
      ? current
      : { pourEntityId: slice.pourEntityId, pourIndex: slice.pourIndex, slices: [] as RiseRateSlice[] };

    band.slices.push(slice);
    if (slice.riseRate !== undefined && (band.peakRiseRate === undefined || slice.riseRate > band.peakRiseRate)) {
      band.peakRiseRate = slice.riseRate;
    }

    return band === current ? bands : [...bands, band];
  }, []);

export interface RiseRateStepPoint {
  hours: number;
  riseRate: number;
}

/**
 * Step points for one band, in canonical metres per hour: the rate is constant across a slice, so each
 * slice contributes its START, and a closing point at the band's end turns the last slice into a tread
 * rather than leaving it as a bare point.
 *
 * Empty for a band with no flow rate - there is no rate to plot, and zero would read as a measurement.
 */
export const bandStepPoints = (band: RiseRateBand): RiseRateStepPoint[] => {
  const rated = band.slices.filter((slice) => slice.riseRate !== undefined);
  if (rated.length === 0) return [];

  const last = rated[rated.length - 1];
  return [
    ...rated.map((slice) => ({ hours: slice.fromHours, riseRate: slice.riseRate! })),
    { hours: last.toHours, riseRate: last.riseRate! },
  ];
};

/** Fill height above the base of the solid over time, for a second axis alongside the rate. */
export const fillHeightPoints = (series: RiseRateSeries): Array<{ hours: number; height: number }> => {
  if (series.slices.length === 0) return [];

  return [
    { hours: series.slices[0].fromHours, height: series.slices[0].fromLevel - series.baseLevel },
    ...series.slices.map((slice) => ({ hours: slice.toHours, height: slice.toLevel - series.baseLevel })),
  ];
};
